import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getAnnotationsData } from "../model/services/getAnnotationsData";
import { Loader } from "@/frontend/shared/ui/Loader/Loader";

import styles from "./AnnotationsSection.module.scss";
import { Typography } from "@/frontend/shared/ui/Typography";
import { NewAnnotation } from "@/frontend/pages/CompanyProfilePage/ui/AnnotationsSection/ui/NewAnnotation/NewAnnotation";
import { Annotation } from "@/frontend/pages/CompanyProfilePage/ui/AnnotationsSection/ui/Annotation/Annotation";
import { reportErrorToSentry } from "@/frontend/shared/lib/reportErrorToSentry/reportErrorToSentry";
import { useNewAnnotationVisible, useSetNewAnnotationVisible } from "@/frontend/atoms/annotations/annotations";
import { ConfirmationModal } from "@/frontend/shared/ui/ConfirmationModal/ConfirmationModal";
import { useState } from "react";
import { deleteAnnotation } from "@/frontend/pages/CompanyProfilePage/ui/AnnotationsSection/model/services/deleteAnnotation";
import { snackbarConstructor } from "@/frontend/shared/ui/CustomNotifications/ui/Snackbar/snackbarConstructor";
import { AxiosError } from "axios";
import { NormalButton } from "@/frontend/shared/ui/Button";
import { PlusIcon } from "@/frontend/shared/assets/icons";
import { useMe } from "@/frontend/shared/lib/hooks/useMe";

type TDataRoomSectionProps = {
  id: number;
  organizationId?: number;
};

export function AnnotationsSection({ id, organizationId }: TDataRoomSectionProps) {
  const { data, isLoading } = useQuery({
    queryKey: ["annotationsData" + id],
    queryFn: async () => await getAnnotationsData({ companyId: id }),
    gcTime: 0,
    refetchOnWindowFocus: true,
    throwOnError: (error) => {
      console.error(error);
      reportErrorToSentry(error, `COMPANY PAGE ANNOTATIONS SECTION: /companies/${id}/annotations`);
      return true;
    }
  });
  const queryClient = useQueryClient();
  const { data: meData } = useMe();

  const [noteIdToDelete, setNoteIdToDelete] = useState(-1);
  const [deletingNote, setDeletingNote] = useState(false);

  const setNewAnnotationEnabled = useSetNewAnnotationVisible();
  const isNewAnnotationEnabled = useNewAnnotationVisible();

  const onDeleteNote = async () => {
    setDeletingNote(true);
    try {
      await deleteAnnotation({ id: noteIdToDelete });
      snackbarConstructor("Note deleted successfully", "normal");
      queryClient.invalidateQueries({ queryKey: ["annotationsData" + id] });
    } catch (error) {
      const msg =
        (error as AxiosError<{ status: number; message: string }>).response?.data?.message || "Failed to delete note";
      reportErrorToSentry(
        new Error(`Failed to delete annotation with id ${noteIdToDelete}`),
        `INVESTOR PORTFOLIO COMPANY PAGE: /portfolio-companies/${id}`
      );
      snackbarConstructor(msg, "error");
    }

    setNoteIdToDelete(-1);
    setDeletingNote(false);
  };

  if (isLoading)
    return (
      <div className={styles.container}>
        <div className={styles.loaderContainer}>
          <Loader baseColor="var(--greyscale150)" />
        </div>
      </div>
    );

  if (!isNewAnnotationEnabled && data?.length === 0) {
    return (
      <div className={styles.container}>
        <div className={styles.emptyContainer}>
          <Typography variant="heading-7" color="greyscale800">
            No notes yet
          </Typography>
          <Typography variant="body-2" color="greyscale800">
            Add a new note to start the conversation
          </Typography>
          <NormalButton
            size="medium"
            variant="secondary"
            className={styles.inviteButton}
            onClick={() => setNewAnnotationEnabled?.(true)}
          >
            <PlusIcon />
            Add a Note
          </NormalButton>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.dataColumn}>
        <Typography variant="heading-6" color="greyscale700">
          Notes
        </Typography>

        <div className={styles.annotationsList}>
          {isNewAnnotationEnabled && <NewAnnotation companyId={id} organizationId={organizationId} />}
          {data?.map((a) => {
            const canModify =
              a.createdBy.id === meData?.id || (meData?.entity === "Investor" && a.createdBy.entity === "Expert");
            return (
              <Annotation
                annotation={a}
                companyId={id}
                key={a.id}
                onDelete={(id) => setNoteIdToDelete(id)}
                canEdit={canModify}
                canDelete={canModify}
              />
            );
          })}
        </div>
      </div>

      <ConfirmationModal
        isOpen={noteIdToDelete !== -1}
        onClose={() => setNoteIdToDelete(-1)}
        onConfirm={onDeleteNote}
        title="Delete Note?"
        content="Are you sure you want to delete this note? This action will also permanently delete all replies associated with it. Once deleted, this cannot be undone."
        isDisabled={deletingNote}
        confirmButtonText="Delete"
        dangerConfirm
      />
    </div>
  );
}
